import { z } from 'zod';

/**
 * THE PAYOUT CLOSED SETS, in a file that imports nothing but zod.
 *
 * `payout-accounts.ts` declared these, and still exports every one of them under
 * the same name. They live here because `lists.ts` types the withdrawal queue's
 * payout fields with them, and `lists.ts` cannot import `payout-accounts.ts`:
 * that file reaches `player-money.ts`, `player-two-factor.ts` and then
 * `player-auth.ts`, which reads `ADMIN_PASSWORD_MINIMUM` from `lists.ts` while
 * it is evaluated. The cycle leaves `lists.ts` half-evaluated at that moment and
 * the package fails to load ("Cannot access 'ADMIN_PASSWORD_MINIMUM' before
 * initialization"). A leaf has no imports to close a cycle with.
 */

/** A registered account's standing. Closed here and CHECKed in sql/1011. */
export const payoutAccountStatus = z.enum(['ACTIVE', 'PENDING_REVIEW', 'REFUSED', 'RETIRED']);
export type PayoutAccountStatus = z.infer<typeof payoutAccountStatus>;

/**
 * Whether a withdrawal's money has left, which is a separate question from the
 * withdrawal's own status: the player is debited at release, whatever happens
 * after. Declared here, in `@dayon/starpay` (where the order the states may move
 * in is kept) and as a CHECK in sql/1011 — adding one is adding it in all three.
 */
export const payoutState = z.enum([
  'NOT_SENT',
  'SENT',
  'RETRY',
  'PROCESSING',
  'PAID',
  'FAILED',
  'REVERSED',
]);
export type PayoutState = z.infer<typeof payoutState>;

/**
 * Why a withdrawal is waiting for a person instead of moving. Declared here, as
 * a type in the API's payout-outcome module and as a CHECK in sql/1011.
 */
export const parkedReason = z.enum([
  'UNVERIFIED_ANSWER',
  'ANSWER_DISAGREES',
  'STATE_UNPROVEN',
  'UNKNOWN_VOCABULARY',
  'DRIVER_FAULT',
  'NO_ORDER_FOUND',
  'ATTEMPTS_EXHAUSTED',
  'NO_WORD_24H',
  'DUPLICATE_AT_BANK',
]);
export type ParkedReason = z.infer<typeof parkedReason>;

/** Whether the holder's name matched the player's verified identity when the account was registered. */
export const nameMatch = z.enum(['MATCHED', 'MISMATCH']);
export type NameMatch = z.infer<typeof nameMatch>;
